import { FormEvent, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  ExtractLabOutput,
  LabAbnormalFlag,
  LabParameter,
  Profile,
} from '@/types/db';
import type { UploadedAttachment } from '@/features/timeline/lib/upload';
import { codeToFlag } from '@/features/timeline/lib/aiFlags';
import { FieldFlagsGroup } from '@/features/timeline/components/flag-chip';
import { saveExtraction } from '@/features/timeline/lib/save';
import { ReviewChrome } from './review-chrome';

interface Props {
  profile: Profile;
  eventId: string;
  attachment: UploadedAttachment;
  extraction: ExtractLabOutput;
}

interface FormState {
  eventDate: string;
  doctorName: string;
  clinicName: string;
  clinicPhone: string;
  clinicAddress: string;
  insuranceCompany: string;
  insuranceClaim: string;
  reportConclusion: string;
  notes: string;
}

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);

function initFormState(e: ExtractLabOutput): FormState {
  return {
    eventDate: e.event_date ?? '',
    doctorName: e.doctor_name ?? '',
    clinicName: e.clinic_name ?? '',
    clinicPhone: e.clinic_phone ?? '',
    clinicAddress: e.clinic_address ?? '',
    insuranceCompany: e.insurance?.company ?? '',
    insuranceClaim: e.insurance?.claim_number ?? '',
    reportConclusion: e.diagnosis ?? '',
    notes: e.notes ?? '',
  };
}

function nullIfEmpty(v: string | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  const t = v.trim();
  return t === '' ? null : t;
}

const FLAG_BADGE: Record<
  LabAbnormalFlag,
  { label: string; classes: string }
> = {
  critical_high: {
    label: 'Critical high',
    classes: 'bg-flag-block/10 text-flag-block border-flag-block/30',
  },
  critical_low: {
    label: 'Critical low',
    classes: 'bg-flag-block/10 text-flag-block border-flag-block/30',
  },
  high: {
    label: 'High',
    classes: 'bg-flag-warn/10 text-flag-warn border-flag-warn/30',
  },
  low: {
    label: 'Low',
    classes: 'bg-flag-warn/10 text-flag-warn border-flag-warn/30',
  },
  abnormal_qualitative: {
    label: 'Abnormal',
    classes: 'bg-flag-warn/10 text-flag-warn border-flag-warn/30',
  },
  normal: {
    label: 'Normal',
    classes: 'bg-evt-lab-soft text-evt-lab-ink border-border',
  },
};

function FlagBadge({ flag }: { flag: LabAbnormalFlag | null }) {
  if (!flag || flag === 'normal') return null;
  const meta = FLAG_BADGE[flag];
  return (
    <span
      className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border ${meta.classes}`}
    >
      {meta.label}
    </span>
  );
}

export function LabReview({ profile, eventId, attachment, extraction }: Props) {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(() => initFormState(extraction));
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const aiFlags = useMemo(
    () => (extraction.flags ?? []).map((c) => codeToFlag(c)),
    [extraction.flags],
  );

  const panels = extraction.report_data?.panels ?? [];
  const parameters = extraction.report_data?.parameters ?? [];

  // Group parameters by panel; ungrouped rows fall under a synthetic bucket.
  const grouped = useMemo(() => {
    const byPanel = new Map<string, LabParameter[]>();
    const ungrouped: LabParameter[] = [];
    for (const p of parameters) {
      if (p.panel) {
        const arr = byPanel.get(p.panel) ?? [];
        arr.push(p);
        byPanel.set(p.panel, arr);
      } else {
        ungrouped.push(p);
      }
    }
    const ordered: { name: string | null; rows: LabParameter[] }[] = [];
    for (const panel of panels) {
      const rows = byPanel.get(panel.panel_name) ?? [];
      ordered.push({ name: panel.panel_name, rows });
      byPanel.delete(panel.panel_name);
    }
    // Any panel mentioned on a parameter but not in panels[] (defensive).
    for (const [name, rows] of byPanel) {
      ordered.push({ name, rows });
    }
    if (ungrouped.length > 0) {
      ordered.push({ name: null, rows: ungrouped });
    }
    return ordered;
  }, [panels, parameters]);

  const requestingDoctor = extraction.report_data?.requesting_doctor ?? null;

  async function handleSave(e?: FormEvent) {
    e?.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSaveError(null);
    setSubmitting(true);
    try {
      await saveExtraction({
        profileId: profile.id,
        eventId,
        eventType: 'lab_report',
        eventDate: nullIfEmpty(form.eventDate),
        doctorName: nullIfEmpty(form.doctorName),
        doctorSpecialty: null,
        clinicName: nullIfEmpty(form.clinicName),
        clinicPhone: nullIfEmpty(form.clinicPhone),
        clinicAddress: nullIfEmpty(form.clinicAddress),
        insuranceCompany: nullIfEmpty(form.insuranceCompany),
        insuranceClaim: nullIfEmpty(form.insuranceClaim),
        diagnosis: nullIfEmpty(form.reportConclusion),
        notes: nullIfEmpty(form.notes),
        title: extraction.title,
        aiFlags,
        medications: [],
        attachments: [attachment],
      });
      navigate(`/profile/${profile.id}`, {
        replace: true,
        state: { justSavedEventId: eventId },
      });
    } catch (err) {
      console.error('[review:lab] save failed', err);
      setSaveError("Couldn't save this record. Please try again.");
      setSubmitting(false);
      submittingRef.current = false;
    }
  }

  return (
    <ReviewChrome
      profile={profile}
      attachment={attachment}
      headerTitle="Review lab report"
      photoCaption="Open the report to cross-check the editable fields below."
      photoAlt="Lab report"
      cancelMessage="Discard this lab report? The photo and any edits will be lost."
      onSubmit={() => handleSave()}
      submitting={submitting}
      saveError={saveError}
    >
      <div className="rounded-lg border border-accent/30 bg-accent-soft p-3 mb-4">
        <p className="text-sm text-ink mb-1">
          <span className="font-semibold">We read this lab report.</span>{' '}
          The parameter table below is read-only — edit basics on the right.
        </p>
        <p className="text-xs text-ink-muted">
          Caremory does not interpret results. Discuss any abnormal value with
          a clinician.
        </p>
      </div>

      {aiFlags.length > 0 && (
        <div className="mb-4">
          <FieldFlagsGroup flags={aiFlags} />
        </div>
      )}

      {/* Read-only panels + parameters */}
      <div className="card p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-ink">
            Tests &amp; results
            <span className="ml-2 text-xs text-ink-muted font-normal">
              read-only
            </span>
          </h2>
          {requestingDoctor && (
            <p className="text-xs text-ink-muted truncate ml-2">
              Req. by {requestingDoctor}
            </p>
          )}
        </div>

        {grouped.length === 0 && (
          <p className="text-sm text-ink-muted">
            No parameters were extracted. Review the photo and edit the fields
            below if needed.
          </p>
        )}

        {grouped.map((panel, panelIdx) => (
          <section
            key={`${panel.name ?? 'ungrouped'}-${panelIdx}`}
            className={
              panelIdx > 0
                ? 'mt-4 pt-4 border-t border-border'
                : ''
            }
          >
            <h3 className="text-sm font-semibold text-evt-lab-ink mb-2">
              {panel.name ?? 'Other parameters'}
            </h3>
            {panel.rows.length === 0 ? (
              <p className="text-xs text-ink-muted">
                Panel reported with no parameter rows.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {panel.rows.map((row, idx) => (
                  <li
                    key={`${panel.name ?? 'u'}-${idx}`}
                    className="py-2 flex items-start gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink truncate">
                        {row.name ?? '—'}
                      </p>
                      {row.reference_range && (
                        <p className="text-xs text-ink-muted">
                          Ref: {row.reference_range}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-ink whitespace-nowrap">
                        {row.value ?? '—'}
                        {row.unit ? (
                          <span className="ml-1 text-xs font-normal text-ink-muted">
                            {row.unit}
                          </span>
                        ) : null}
                      </p>
                      <div className="mt-1">
                        <FlagBadge flag={row.abnormal_flag} />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {/* Editable basics */}
      <form onSubmit={handleSave} className="card p-5 space-y-4">
        <h2 className="text-sm font-bold text-ink">Basics</h2>

        <div>
          <label htmlFor="eventDate" className="label">Date</label>
          <input
            id="eventDate"
            type="date"
            value={form.eventDate}
            max={TODAY_ISO()}
            onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
            className="input"
          />
        </div>

        <div>
          <label htmlFor="doctorName" className="label">Requesting doctor</label>
          <input
            id="doctorName"
            type="text"
            value={form.doctorName}
            onChange={(e) => setForm({ ...form, doctorName: e.target.value })}
            className="input"
          />
        </div>

        <div>
          <label htmlFor="clinicName" className="label">Lab / clinic</label>
          <input
            id="clinicName"
            type="text"
            value={form.clinicName}
            onChange={(e) => setForm({ ...form, clinicName: e.target.value })}
            className="input"
          />
        </div>

        <div>
          <label htmlFor="clinicPhone" className="label">Phone</label>
          <input
            id="clinicPhone"
            type="tel"
            value={form.clinicPhone}
            placeholder="e.g. +20 2 1234 5678"
            onChange={(e) => setForm({ ...form, clinicPhone: e.target.value })}
            className="input"
          />
        </div>

        <div>
          <label htmlFor="clinicAddress" className="label">Address</label>
          <input
            id="clinicAddress"
            type="text"
            value={form.clinicAddress}
            onChange={(e) =>
              setForm({ ...form, clinicAddress: e.target.value })
            }
            className="input"
          />
        </div>

        <div>
          <label htmlFor="reportConclusion" className="label">
            Report conclusion
          </label>
          <textarea
            id="reportConclusion"
            value={form.reportConclusion}
            onChange={(e) =>
              setForm({ ...form, reportConclusion: e.target.value })
            }
            placeholder="Lab's printed impression / interpretation, if present."
            className="input py-2 min-h-[72px]"
            rows={3}
          />
        </div>

        <div>
          <label htmlFor="notes" className="label">Notes</label>
          <textarea
            id="notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Auto-filled with lab-flagged abnormals + panel list."
            className="input py-2 min-h-[72px]"
            rows={3}
          />
        </div>

        <details className="rounded-lg border border-border bg-canvas">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-ink-muted">
            Insurance (optional)
          </summary>
          <div className="px-3 pb-3 pt-1 space-y-3">
            <div>
              <label htmlFor="insuranceCompany" className="label">
                Insurance company
              </label>
              <input
                id="insuranceCompany"
                type="text"
                value={form.insuranceCompany}
                onChange={(e) =>
                  setForm({ ...form, insuranceCompany: e.target.value })
                }
                className="input"
              />
            </div>
            <div>
              <label htmlFor="insuranceClaim" className="label">
                Claim / policy #
              </label>
              <input
                id="insuranceClaim"
                type="text"
                value={form.insuranceClaim}
                onChange={(e) =>
                  setForm({ ...form, insuranceClaim: e.target.value })
                }
                className="input"
              />
            </div>
          </div>
        </details>
      </form>
    </ReviewChrome>
  );
}
